import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {TextField, TextFieldProps} from '@mui/material';
import {DateObject} from 'react-multi-date-picker';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import {LocalizedDatePicker} from './LocalizedCalendar';

const displayDate = (value: string) => {
    const [date, time] = value.split('T');
    return date ? date.split('-').reverse().join('.') + (time ? ' ' + time.slice(0, 5) : '') : '';
};

export const LocalizedDateField = ({type, value, onChange, inputProps, InputLabelProps, showHolidayLegend = false, workshopDates, ...props}: TextFieldProps & {showHolidayLegend?: boolean; workshopDates?: readonly string[]}) => {
    const withTime = type === 'datetime-local';
    const [portalTarget, setPortalTarget] = useState<HTMLElement>();
    const attachField = useCallback((element: HTMLDivElement | null) => {
        // Escape the scrolling paper while staying inside MUI's dialog focus trap.
        setPortalTarget(element?.closest<HTMLElement>('.MuiDialog-container') ?? undefined);
    }, []);
    const [uncontrolledValue, setUncontrolledValue] = useState('');
    const isoValue = typeof value === 'string' ? value : uncontrolledValue;
    const [draft, setDraft] = useState(() => displayDate(isoValue));
    const emittedValue = useRef<string>();
    useEffect(() => {
        if (isoValue !== emittedValue.current) setDraft(displayDate(isoValue));
    }, [isoValue]);
    const emit = (value: string) => {
        emittedValue.current = value;
        setUncontrolledValue(value);
        onChange?.({target: {value, name: props.name, id: props.id}, currentTarget: {value}} as ChangeEvent<HTMLInputElement>);
    };

    return <LocalizedDatePicker showHolidayLegend={showHolidayLegend} workshopDates={workshopDates} value={isoValue || null} format={withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'}
        portal={!!portalTarget} portalTarget={portalTarget} zIndex={1400}
        disabled={props.disabled} containerStyle={{width: props.fullWidth ? '100%' : 'auto'}}
        plugins={withTime ? [<TimePicker key="time" position="bottom" hideSeconds/>] : []}
        onChange={(value) => {
            const date = Array.isArray(value) ? value[0] : value;
            if (date instanceof DateObject) {
                const iso = date.format(withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD');
                setDraft(displayDate(iso));
                emit(iso);
            } else {setDraft(''); emit('');}
        }}
        render={(_, openCalendar) => <TextField {...props} ref={attachField} type="text" value={draft}
            placeholder={withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'}
            InputLabelProps={{...InputLabelProps, shrink: true}}
            inputProps={{...inputProps, style: {...inputProps?.style, cursor: 'default'}}}
            onClick={openCalendar} onChange={(event) => {
                const text = event.target.value;
                setDraft(text);
                if (!text) {emit(''); return;}
                const match = /^(\d{2})\.(\d{2})\.(\d{4})(?: (\d{2}):(\d{2}))?$/.exec(text);
                if (!match || (withTime && !match[4])) {emit(''); return;}
                const [, day, month, year, hour, minute] = match;
                const iso = year + '-' + month + '-' + day + (withTime ? 'T' + hour + ':' + minute : '');
                const parsed = new Date(iso + (withTime ? ':00Z' : 'T00:00:00Z'));
                if (!Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, withTime ? 16 : 10) === iso) emit(iso);
                else emit('');
            }}/>}
    />;
};
